import { useEffect, useId, useRef } from "react";
import api, { formatError, mutationFailure } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Tag } from "lucide-react";

// OP-026. Same bounds as PATCH /saved/{video_id}; the server stays authoritative and returns the stored tags.
export const SAVED_TAG_MAX_LENGTH = 40;
export const SAVED_TAG_MAX_COUNT = 20;

export const savedTagsOf = (video) => (Array.isArray(video?.saved_tags) ? video.saved_tags.filter((t) => typeof t === "string" && t.trim()) : []);
export const tagKey = (tag) => tag.trim().replace(/\s+/g, " ").toLowerCase();

// Commas and semicolons separate tags. Empty entries are ignored; case-insensitive duplicates keep the first spelling.
export function parseTagInput(text) {
  const tags = [], duplicates = [], seen = new Set();
  let error = "";
  for (const part of text.split(/[,;]/)) {
    const tag = part.trim().replace(/\s+/g, " ");
    if (!tag) continue;
    if (tag.length > SAVED_TAG_MAX_LENGTH) error = error || `« ${tag.slice(0, 20)}… » dépasse ${SAVED_TAG_MAX_LENGTH} caractères.`;
    // eslint-disable-next-line no-control-regex
    else if (/[\u0000-\u001f\u007f]/.test(tag)) error = error || "Un tag ne peut pas contenir de caractère de contrôle.";
    const key = tag.toLowerCase();
    if (seen.has(key)) duplicates.push(tag);
    else { seen.add(key); tags.push(tag); }
  }
  if (!error && tags.length > SAVED_TAG_MAX_COUNT) error = `${SAVED_TAG_MAX_COUNT} tags au maximum par référence (${tags.length} saisis).`;
  return { tags, duplicates, error };
}

const sameTags = (a, b) => a.length === b.length && a.every((t, i) => t === b[i]);

export function TagChips({ tags, label, testid }) {
  if (!tags.length) return <p className="text-xs text-muted-foreground" data-testid={testid ? `${testid}-none` : undefined}>Aucun tag</p>;
  return <ul aria-label={label} className="flex flex-wrap gap-1" data-testid={testid}>
    {tags.map((t) => <li key={t} className="inline-flex max-w-full items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium"><Tag className="h-3 w-3 shrink-0" aria-hidden="true" /><span className="truncate">{t}</span></li>)}
  </ul>;
}

// Tags of one saved reference. Only tags are sent: the note, saved_at and saved_by stay as stored.
// The editor state (draft, error, pending request) lives in the page, scoped to the current read, so it
// survives a reconciliation re-read; `editors` is that page-level store.
// OP-026: the draft keeps the tag revision read when it was opened. The server applies it only if the
// tags still have that revision; otherwise (409) the draft stays and a re-read is offered, never a resend.
export default function SavedTags({ video, canEdit, editor, editors, onSaved, onUncertain, onReread, onClosed, outsideFilter = false }) {
  const id = useId();
  const tags = savedTagsOf(video);
  const editing = !!editor;
  const saving = !!editor?.saving;
  const draft = editor?.draft ?? "";
  const parsed = parseTagInput(draft);
  const inputRef = useRef(null);
  const editRef = useRef(null);
  const focusInput = useRef(false);
  const reopenFocus = useRef(false);
  const title = video.title || "sans titre";

  useEffect(() => {
    if (editing && focusInput.current) { focusInput.current = false; inputRef.current?.focus(); }
    else if (!editing && reopenFocus.current) { reopenFocus.current = false; editRef.current?.focus(); }
  }, [editing]);

  const open = () => { focusInput.current = true; editors.open(video.id, { draft: tags.join(", "), title, revision: video.saved_revisions?.tags }); };
  const close = (videoId = video.id) => { reopenFocus.current = true; editors.close(videoId); onClosed?.(videoId, title); };

  const submit = async (event) => {
    event.preventDefault();
    if (!canEdit || !editing || parsed.error) return;
    if (sameTags(parsed.tags, tags)) { if (!saving) close(); return; }
    const videoId = video.id;
    if (!editors.begin(videoId)) return;
    editors.patch(videoId, { saving: true, error: "", uncertain: false, conflict: false });
    try {
      const { data } = await api.patch(`/saved/${encodeURIComponent(videoId)}`, { tags: parsed.tags, expected_tags_revision: editor.revision });
      if (!Array.isArray(data?.tags)) throw Object.assign(new Error("Réponse du serveur inutilisable."), { response: { status: 500 } });
      // Published from the server's answer (stored tags and their new revision), never from the draft.
      onSaved(videoId, data);
      close(videoId);
      // No success message once the page shows another workspace or account.
      if (editors.current()) toast.success("Tags enregistrés");
    } catch (err) {
      // The draft stays in the page store. An unknown outcome re-reads the list, never resends.
      const kind = mutationFailure(err);
      const detail = formatError(err);
      const message = kind === "refused" ? `Le serveur a refusé les tags : ${detail}`
        : kind === "conflict" ? `${detail} Votre brouillon est conservé : relisez la référence avant de décider.`
        : kind === "unknown" ? `Résultat inconnu (${detail}) : les tags ont peut-être été enregistrés. Vérifiez la liste avant de réessayer.`
        : detail;
      editors.patch(videoId, { error: message, uncertain: kind === "unknown", conflict: kind === "conflict" });
      if (editors.current()) toast.error(message);
      if (kind === "unknown") onUncertain?.();
      // A reference removed meanwhile is not re-created: the re-read shows it gone and keeps the draft.
      if (err?.response?.data?.detail?.code === "saved_missing") onReread?.(videoId);
    } finally {
      editors.end(videoId);
      editors.patch(videoId, { saving: false });
    }
  };

  // After an unknown outcome, the re-read list is an observation, not a confirmation of this request.
  const reconciled = editor?.uncertain && !saving && !parsed.error && sameTags(parsed.tags, tags);
  const describedBy = [`${id}-hint`, parsed.error && `${id}-invalid`, editor?.error && `${id}-error`].filter(Boolean).join(" ");
  return <div className="space-y-1.5 px-1" data-testid={`saved-tags-${video.id}`}>
    {outsideFilter && <p className="text-xs text-muted-foreground" data-testid={`saved-tags-outside-filter-${video.id}`}>Ne correspond plus aux filtres actifs : la carte reste affichée tant que l’édition est ouverte.</p>}
    {!editing && <TagChips tags={tags} label={`Tags de « ${title} »`} testid={`saved-tags-list-${video.id}`} />}
    {canEdit && !editing && <button type="button" ref={editRef} onClick={open} aria-label={`Modifier les tags de « ${title} »`} data-testid={`saved-tags-edit-${video.id}`} className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-xs text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"><Tag className="h-3.5 w-3.5" aria-hidden="true" />Modifier les tags</button>}
    {canEdit && editing && <form onSubmit={submit} aria-label={`Tags de « ${title} »`} className="space-y-1.5" data-testid={`saved-tags-form-${video.id}`} onKeyDown={(e) => { if (e.key === "Escape" && !saving) { e.preventDefault(); close(); } }}>
      <label htmlFor={`${id}-input`} className="block text-xs font-medium">Tags, séparés par des virgules</label>
      <Input id={`${id}-input`} ref={inputRef} value={draft} onChange={(e) => editors.patch(video.id, { draft: e.target.value })} readOnly={saving} aria-invalid={!!parsed.error} aria-describedby={describedBy} className="h-8 text-xs md:text-xs" data-testid={`saved-tags-input-${video.id}`} />
      <p id={`${id}-hint`} className="text-xs text-muted-foreground">Videz le champ pour retirer tous les tags. {SAVED_TAG_MAX_COUNT} tags au maximum, {SAVED_TAG_MAX_LENGTH} caractères chacun.</p>
      <div className="text-xs" data-testid={`saved-tags-preview-${video.id}`}><span className="text-muted-foreground">Après enregistrement : </span>{parsed.tags.length ? parsed.tags.join(" · ") : "aucun tag"}</div>
      {parsed.duplicates.length > 0 && <p className="text-xs text-muted-foreground" data-testid={`saved-tags-duplicates-${video.id}`}>Doublons ignorés : {parsed.duplicates.join(", ")}</p>}
      {parsed.error && <p id={`${id}-invalid`} className="text-xs text-destructive" data-testid={`saved-tags-invalid-${video.id}`}>{parsed.error}</p>}
      {editor?.error && <p id={`${id}-error`} role="alert" className="text-xs text-destructive break-words" data-testid={`saved-tags-error-${video.id}`}>{editor.error}</p>}
      {reconciled && <p className="text-xs text-muted-foreground" data-testid={`saved-tags-reconciled-${video.id}`}>La liste relue contient exactement ces tags. Vous pouvez fermer l’éditeur.</p>}
      {editor?.conflict && !saving && <Button type="button" size="sm" variant="outline" onClick={() => onReread?.(video.id)} data-testid={`saved-tags-reread-${video.id}`}>Relire la référence</Button>}
      {editor?.rebased && !editor?.conflict && <p className="text-xs text-muted-foreground" data-testid={`saved-tags-rebased-${video.id}`}>Tags enregistrés actuellement : {editor.rebased.length ? editor.rebased.join(" · ") : "aucun tag"}. « Enregistrer » les remplacera par votre brouillon.</p>}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={saving || !!parsed.error} data-testid={`saved-tags-submit-${video.id}`}>{saving ? "Enregistrement…" : "Enregistrer"}</Button>
        <Button type="button" size="sm" variant="outline" disabled={saving} onClick={() => close()} data-testid={`saved-tags-cancel-${video.id}`}>Annuler</Button>
      </div>
    </form>}
  </div>;
}
